import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useDeferredValue,
} from 'react';
import {
  Badge,
  Box,
  Button,
  EmptyContent,
  Flex,
  Heading,
  Popover,
  Progress,
  Tabs,
  Text,
  VisuallyHidden,
} from '@/components/ui';
import { FileProgressItem, FileProgressList } from '@/components/file-progress-list';
import { useUploadQueue } from '@/contexts/UploadQueueContext';
import { useUploadQueueWidget } from '@/contexts/UploadQueueWidgetContext';
import { QueuedFile } from '@/types/uploadQueue';
import {
  calculateQueueStats,
  calculateTotalProgress,
  getErrorFiles,
} from '@/utils/uploadQueueUtils';

import {
  IconAlertCircle,
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconClock,
  IconLoader2,
  IconUpload,
} from '@tabler/icons-react';

import UploadQueueControls from './UploadQueueControls';

type UploadState =
  | 'empty'
  | 'waiting'
  | 'uploading'
  | 'success'
  | 'error'
  | 'mixed';

const UploadQueueWidget: React.FC = React.memo(() => {
  const { state, removeFile, retryFile, retryAllErrors, startUpload } = useUploadQueue();
  const { isOpen, openWidget, closeWidget } = useUploadQueueWidget();
  const [activeTab, setActiveTab] = useState('queue');
  const [shouldPulse, setShouldPulse] = useState(false);
  const prevCompletedCountRef = useRef<number | null>(null);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Defer file list updates to keep UI responsive during rapid upload progress
  const deferredFiles = useDeferredValue(state.files);

  const stats = useMemo(
    () => calculateQueueStats(deferredFiles),
    [deferredFiles]
  );
  const errorFiles = useMemo(
    () => getErrorFiles(deferredFiles),
    [deferredFiles]
  );
  const completedFiles = useMemo(
    () => deferredFiles.filter(f => f.status === 'completed'),
    [deferredFiles]
  );
  const queueFiles = useMemo(
    () =>
      deferredFiles.filter(f => ['pending', 'uploading'].includes(f.status)),
    [deferredFiles]
  );
  const remainingCount = queueFiles.length;
  const displayTotalProgress = useMemo(
    () => calculateTotalProgress(deferredFiles),
    [deferredFiles]
  );

  const toFileProgressItem = useCallback(
    (queuedFile: QueuedFile): FileProgressItem => {
      const status: FileProgressItem['status'] = (() => {
        switch (queuedFile.status) {
          case 'uploading':
            return 'processing';
          case 'completed':
            return 'completed';
          case 'cancelled':
            return 'cancelled';
          case 'error':
            return 'error';
          case 'pending':
          default:
            return 'pending';
        }
      })();

      return {
        id: queuedFile.id,
        name: queuedFile.file.name,
        size: queuedFile.file.size,
        status,
        progress: queuedFile.progress,
        error: queuedFile.error,
      };
    },
    []
  );

  const currentProcessingFileId = useMemo(() => {
    return state.files[state.currentUploadIndex]?.id;
  }, [state.currentUploadIndex, state.files]);

  const queueProgressItems = useMemo(
    () => queueFiles.map(toFileProgressItem),
    [queueFiles, toFileProgressItem]
  );
  const completedProgressItems = useMemo(
    () => completedFiles.map(toFileProgressItem),
    [completedFiles, toFileProgressItem]
  );
  const errorProgressItems = useMemo(
    () => errorFiles.map(toFileProgressItem),
    [errorFiles, toFileProgressItem]
  );

  const queueCurrentProcessingIndex = useMemo(() => {
    if (!currentProcessingFileId) return undefined;
    const idx = queueProgressItems.findIndex(f => f.id === currentProcessingFileId);
    return idx >= 0 ? idx : undefined;
  }, [currentProcessingFileId, queueProgressItems]);

  useEffect(() => {
    const completedCount = completedFiles.length;

    // Avoid pulsing on initial mount / restore-from-storage.
    if (prevCompletedCountRef.current === null) {
      prevCompletedCountRef.current = completedCount;
      return;
    }

    if (completedCount > prevCompletedCountRef.current) {
      setShouldPulse(true);

      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }

      pulseTimeoutRef.current = setTimeout(() => {
        setShouldPulse(false);
        pulseTimeoutRef.current = null;
      }, 650);
    }

    prevCompletedCountRef.current = completedCount;

    return () => {
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
        pulseTimeoutRef.current = null;
      }
    };
  }, [completedFiles.length]);

  // Determine current upload state
  const uploadState: UploadState = useMemo(() => {
    if (stats.total === 0) return 'empty';

    const hasUploading = stats.uploading > 0;
    const hasWaiting = stats.pending > 0;
    const hasFailed = stats.error > 0;
    const hasCompleted = stats.completed > 0;

    if (hasUploading || (hasWaiting && state.isUploading)) return 'uploading';
    if (hasWaiting) return 'waiting';
    if (hasFailed && hasCompleted) return 'mixed';
    if (hasFailed) return 'error';
    if (hasCompleted) return 'success';

    return 'empty';
  }, [
    stats.total,
    stats.uploading,
    stats.pending,
    stats.error,
    stats.completed,
    state.isUploading,
  ]);

  // Get state configuration
  const stateConfig = useMemo(() => {
    const configs = {
      empty: {
        icon: IconUpload,
        color: 'default' as const,
        badgeColor: 'gray' as const,
        tooltip: 'No files in upload queue',
        showProgress: false,
      },
      waiting: {
        icon: IconClock,
        color: 'primary' as const,
        badgeColor: 'warning' as const,
        tooltip: `${stats.pending} files waiting for upload`,
        showProgress: false,
      },
      uploading: {
        icon: IconLoader2,
        color: 'primary' as const,
        badgeColor: 'blue' as const,
        tooltip: `Uploading ${stats.uploading} files, ${remainingCount} remaining...`,
        showProgress: true,
      },
      success: {
        icon: IconCheck,
        color: 'success' as const,
        badgeColor: 'green' as const,
        tooltip: 'All files uploaded successfully!',
        showProgress: false,
      },
      error: {
        icon: IconAlertCircle,
        color: 'error' as const,
        badgeColor: 'red' as const,
        tooltip: `${stats.error} uploads failed`,
        showProgress: false,
      },
      mixed: {
        icon: IconAlertTriangle,
        color: 'warning' as const,
        badgeColor: 'orange' as const,
        tooltip: 'Some uploads failed',
        showProgress: false,
      },
    };
    return configs[uploadState];
  }, [
    uploadState,
    stats.pending,
    stats.uploading,
    stats.total,
    stats.error,
    remainingCount,
  ]);

  const IconComponent = stateConfig.icon;
  const isRatioBadge = uploadState === 'mixed' || uploadState === 'error';

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={open => (open ? openWidget() : closeWidget())}
    >
      <Popover.Trigger>
        <Button
          size="small"
          variant="soft"
          color={stateConfig.color}
          className={`upload-queue-widget__trigger hover-effect ${shouldPulse ? 'upload-queue-widget__trigger--pulse' : ''}`}
          labelProp={{
            color: 'accent',
          }}
        >
          <Flex align="center" gap="2">
            {/* Icon with animation for uploading state */}
            <IconComponent
              className={
                uploadState === 'uploading'
                  ? 'upload-queue-widget__spinner'
                  : undefined
              }
            />

            {/* Badge with file count or status */}
            {stats.total > 0 &&
              (remainingCount > 0 ||
                uploadState === 'mixed' ||
                uploadState === 'error') && (
              <Badge
                size="xsmall"
                color={stateConfig.badgeColor}
                variant="solid"
                className={`upload-queue-widget__badge ${isRatioBadge ? 'upload-queue-widget__badge--ratio' : 'upload-queue-widget__badge--count'}`}
              >
                {uploadState === 'mixed' || uploadState === 'error'
                  ? `${stats.completed}/${stats.error}`
                  : remainingCount}
              </Badge>
            )}

            {/* Dropdown indicator */}
            <IconChevronDown
              className={`upload-queue-widget__dropdown-arrow ${isOpen ? 'upload-queue-widget__dropdown-arrow--open' : ''}`}
            />
          </Flex>

          {/* Status indicator dot */}
          {uploadState !== 'empty' && (
            <Box
              className={`upload-queue-widget__status-dot upload-queue-widget__status-dot--${uploadState}`}
            />
          )}

          {/* Progress indicator for uploading state */}
          {stateConfig.showProgress && (
            <Box className="upload-queue-widget__progress-bar">
              <Box
                className="upload-queue-widget__progress-bar-fill"
                style={{
                  width: `${Math.min(100, Math.max(0, displayTotalProgress))}%`,
                }}
              />
            </Box>
          )}
        </Button>
      </Popover.Trigger>

      <Popover.Content
        width="560px"
        maxWidth="90vw"
        maxHeight="600px"
        side="bottom"
        align="end"
        className="upload-queue-widget__content"
      >
        <Box>
          {/* Header */}
          <Box p="0" style={{ borderBottom: '1px solid var(--gray-4)' }}>
            <Flex direction="column" gap="3" mb="3">
              <Flex justify="between" align="center">
                <Heading size="xsmall">Upload Queue</Heading>
                {stats.total > 0 && (
                  <Text size="2xsmall" color="gray">
                    {stats.completed}/{stats.total} completed
                  </Text>
                )}
              </Flex>

              {/* Compact Analytics */}
              <VisuallyHidden>
                {stats.total > 0 && (
                  <Flex
                    align="center"
                    justify="between"
                    p="2"
                    style={{
                      backgroundColor: 'var(--gray-2)',
                      borderRadius: 'var(--radius-2)',
                    }}
                  >
                    <Flex align="center" gap="3">
                      <Flex align="center" gap="1">
                        <Text size="2xsmall" weight="medium" color="blue">
                          {queueFiles.length}
                        </Text>
                        <Text size="2xsmall" color="gray">
                          Queue
                        </Text>
                      </Flex>
                      <Flex align="center" gap="1">
                        <Text size="2xsmall" weight="medium" color="green">
                          {completedFiles.length}
                        </Text>
                        <Text size="2xsmall" color="gray">
                          Done
                        </Text>
                      </Flex>
                      <Flex align="center" gap="1">
                        <Text size="2xsmall" weight="medium" color="red">
                          {errorFiles.length}
                        </Text>
                        <Text size="2xsmall" color="gray">
                          Failed
                        </Text>
                      </Flex>
                    </Flex>

                    {/* Overall progress indicator */}
                    {stateConfig.showProgress && (
                      <Flex align="center" gap="2">
                        <Text size="2xsmall" color="blue">
                          {Math.round(displayTotalProgress)}%
                        </Text>
                        <Box style={{ width: 24, height: 24 }}>
                          <Progress
                            value={displayTotalProgress}
                            size="small"
                            style={{
                              width: '100%',
                              height: '100%',
                            }}
                          />
                        </Box>
                      </Flex>
                    )}
                  </Flex>
                )}
              </VisuallyHidden>
            </Flex>
          </Box>

          {/* Content */}
          {stats.total === 0 ? (
            <EmptyContent
              size="small"
              icon={<IconUpload />}
              title="No files in upload queue"
              description="Select files to add them to the upload queue."
            />
          ) : (
            <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
              <Tabs.List
                color="secondary"
                highContrast
                style={{ margin: '8px' }}
              >
                <Tabs.Trigger value="queue">
                  <Flex align="center" gap="1">
                    Queue
                    {queueFiles.length > 0 && (
                      <Badge size="xsmall" color="info" variant="solid">
                        {queueFiles.length}
                      </Badge>
                    )}
                  </Flex>
                </Tabs.Trigger>

                <Tabs.Trigger value="completed">
                  <Flex align="center" gap="1">
                    Completed
                    {completedFiles.length > 0 && (
                      <Badge size="xsmall" color="success" variant="solid">
                        {completedFiles.length}
                      </Badge>
                    )}
                  </Flex>
                </Tabs.Trigger>

                <Tabs.Trigger value="failed">
                  <Flex align="center" gap="1">
                    Failed
                    {errorFiles.length > 0 && (
                      <Badge size="xsmall" color="error" variant="solid">
                        {errorFiles.length}
                      </Badge>
                    )}
                  </Flex>
                </Tabs.Trigger>
              </Tabs.List>

              <Tabs.Content value="queue" style={{ padding: '16px' }}>
                {queueFiles.length === 0 ? (
                  <Text
                    size="2"
                    color="gray"
                    style={{ textAlign: 'center', padding: '16px 0' }}
                  >
                    No files in queue
                  </Text>
                ) : (
                  <FileProgressList
                    files={queueProgressItems}
                    currentProcessingIndex={queueCurrentProcessingIndex}
                    isProcessing={state.isUploading}
                    maxHeight="320px"
                    onRemoveFile={(id) => {
                      if (typeof id === 'string') removeFile(id);
                    }}
                    onRetryFile={(id) => {
                      if (typeof id === 'string') retryFile(id);
                    }}
                  />
                )}
              </Tabs.Content>

              <Tabs.Content value="completed" style={{ padding: '16px' }}>
                {completedFiles.length === 0 ? (
                  <Text
                    size="2"
                    color="gray"
                    style={{ textAlign: 'center', padding: '16px 0' }}
                  >
                    No completed uploads
                  </Text>
                ) : (
                  <FileProgressList
                    files={completedProgressItems}
                    isProcessing={state.isUploading}
                    maxHeight="320px"
                    onRemoveFile={(id) => {
                      if (typeof id === 'string') removeFile(id);
                    }}
                  />
                )}
              </Tabs.Content>

              <Tabs.Content value="failed" style={{ padding: '16px' }}>
                {errorFiles.length === 0 ? (
                  <Text
                    size="2"
                    color="gray"
                    style={{ textAlign: 'center', padding: '16px 0' }}
                  >
                    No failed uploads
                  </Text>
                ) : (
                  <Box>
                    <Box mb="3">
                      <Button
                        size="small"
                        variant="soft"
                        color="warning"
                        onClick={() => {
                          retryAllErrors();
                          startUpload();
                        }}
                      >
                        Retry All Errors ({errorFiles.length})
                      </Button>
                    </Box>
                    <FileProgressList
                      files={errorProgressItems}
                      isProcessing={state.isUploading}
                      maxHeight="320px"
                      onRemoveFile={(id) => {
                        if (typeof id === 'string') removeFile(id);
                      }}
                      onRetryFile={(id) => {
                        if (typeof id === 'string') retryFile(id);
                      }}
                    />
                  </Box>
                )}
              </Tabs.Content>
            </Tabs.Root>
          )}

          <Box py="2" style={{ borderTop: '1px solid var(--gray-4)' }}>
            {/* Control Buttons */}
            <UploadQueueControls />
          </Box>
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
});

export default UploadQueueWidget;
